import dayjs from 'dayjs'
import localeData from 'dayjs/plugin/localeData'
import { storeToRefs } from 'pinia'
import type { QNotifyCreateOptions, QNotifyOptions } from 'quasar'
import { Notify, type QNotifyUpdateOptions } from 'quasar'
import { computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

import tNotifications from '@/i18n/translations/components/notifications.json'
import { useAppStore } from '@/stores/modules/app'
import { useAuthStore } from '@/stores/modules/auth'

dayjs.extend(localeData)

export const useNotifications = () => {
  const notifyDeleteUser = () => {
    const appStore = useAppStore()
    const route = useRoute()

    const { locale, t } = useI18n(tNotifications)

    const { notificationDismissDeleteRequest, user } =
      storeToRefs(useAuthStore())

    let dismissNotification: ((props?: QNotifyUpdateOptions) => void) | null =
      null

    const clearNotification = () => {
      if (dismissNotification) {
        dismissNotification()
        dismissNotification = null
      }
    }

    const requestedDeletionAt = computed(
      () => user.value?.requested_deletion_at,
    )

    const deletionDueDate = computed(() =>
      dayjs(requestedDeletionAt.value)
        .add(appStore.config?.user_request_deletion_days || 0, 'days')
        .locale(locale.value)
        .format('D MMM'),
    )

    const message = computed(() =>
      t('notifyDeleteUser.message', { deletionDueDate: deletionDueDate.value }),
    )
    const caption = computed(() => t('notifyDeleteUser.caption'))

    const classes =
      'q-px-md q-py-xs no-shadow text-body1 bg-warning-1 text-warning border-warning border-radius-sm'

    watch(locale, clearNotification, { immediate: true })

    const stopWatcher = watch(
      [route, requestedDeletionAt, notificationDismissDeleteRequest],
      (v) => {
        if (v[2]) {
          clearNotification()
          stopWatcher()

          return
        }

        if (v[0].meta.notificationDeleteRequest && v[1]) {
          dismissNotification = Notify.create({
            actions: [
              {
                class: 'bg-warning',
                color: 'white',
                dense: true,
                handler: () => {
                  notificationDismissDeleteRequest.value = true
                },
                icon: 'cancel',
                padding: '0.25rem',
                round: true,
                size: 'sm',
              },
            ],
            badgeClass: 'hidden',
            caption: caption.value,
            classes,
            icon: 'warning',
            iconSize: 'sm',
            message: message.value,
            position: 'top',
            timeout: 0,
          })
        } else {
          clearNotification()
        }
      },
    )
  }

  const notifyHttpConflict = (message: string) => {
    Notify.create({
      actions: [
        {
          color: 'white',
          dense: true,
          icon: 'cancel',
        },
      ],
      attrs: {
        style: {
          'max-width': '580px',
        },
      },
      color: 'negative',
      icon: 'warning',
      message,
      position: 'center',
      timeout: 0,
    })
  }

  const makeNotify = (options?: QNotifyOptions) => {
    const classes =
      'q-px-md q-py-xs no-shadow text-body1 bg-positive-1 text-positive border-positive border-radius-sm'

    const defaults: QNotifyCreateOptions = {
      classes,
      icon: 'check',
      iconSize: 'xs',
      position: 'bottom-right',
      timeout: 500,
    }

    const opts = { ...defaults, ...options }

    Notify.create(opts)
  }

  const notifySsoError = () => {
    const route = useRoute()

    const { t, te } = useI18n(tNotifications)

    watch(
      () => route.query.sso_error,
      (val) => {
        const errorKey = val?.toString()
        if (!errorKey) return

        const { sso_error: _, ...remaining } = route.query
        const replace = Object.fromEntries(
          Object.entries(remaining).filter(([, v]) => typeof v === 'string'),
        )

        const message = te(`ssoError.${errorKey}`)
          ? t(`ssoError.${errorKey}`, replace)
          : t('ssoError.generic')

        Notify.create({
          actions: [
            {
              color: 'white',
              dense: true,
              icon: 'cancel',
              round: true,
              size: 'sm',
            },
          ],
          classes:
            'q-px-md q-py-xs no-shadow text-body1 bg-warning-1 text-warning border-warning border-radius-sm',
          icon: 'info',
          iconSize: 'sm',
          message,
          position: 'bottom-right',
          timeout: 8000,
        })
      },
      { immediate: true },
    )
  }

  return {
    makeNotify,
    notifyDeleteUser,
    notifyHttpConflict,
    notifySsoError,
  }
}
